import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/auth";
import { trackWaitlistSubmit } from "@/lib/analytics";
import { CheckCircle2 } from "lucide-react";

const CATEGORY_OPTIONS = [
  "Hair",
  "Barber",
  "Makeup",
  "Brows & Lashes",
  "Skin & Injectables",
  "Spray Tans",
  "Nails",
  "Massage & Body",
  "Wellness",
];

const empty = {
  salon_name: "",
  owner_name: "",
  email: "",
  phone: "",
  instagram: "",
  category: "",
};

/**
 * Inline waitlist capture modal for the Expansion section.
 * Replaces the previous `mailto:` handoff so signups land directly in the
 * Book Away CRM (Mongo `waitlist` collection) and trigger a confirmation email.
 */
export default function WaitlistDialog({ open, onOpenChange, city }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Reset the form each time the modal opens for a (potentially different) city.
  useEffect(() => {
    if (open) {
      setForm(empty);
      setError("");
      setDone(false);
    }
  }, [open, city]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/waitlist", {
        business_name: form.salon_name.trim(),
        contact_name: form.owner_name.trim(),
        email: form.email.trim(),
        city,
        business_type: form.category,
        phone: form.phone.trim() || null,
        instagram: form.instagram.trim() || null,
      });
      setDone(true);
      trackWaitlistSubmit(city, form.category);
      toast.success(`You're on the ${city} waitlist.`);
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="border-black/10 bg-[#F7F5F2] text-[#2A2724] sm:max-w-md"
      >
        {done ? (
          <div
            data-testid="waitlist-modal-success"
            className="flex flex-col items-center gap-3 py-6 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-gold" />
            <DialogTitle className="font-serif text-2xl font-normal">
              Your spot is being held.
            </DialogTitle>
            <p className="max-w-sm text-sm text-[#6B6B6B]">
              We'll email <span className="text-gold">{form.email}</span> the
              moment Book Away opens in {city}. Foundation Members get priority
              placement and a lifetime discount.
            </p>
            <Button
              data-testid="waitlist-modal-close"
              onClick={() => onOpenChange(false)}
              className="mt-2 rounded-full bg-gold px-6 py-5 text-sm font-medium text-black hover:bg-[#AA8627]"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <p className="overline">Foundation waitlist</p>
              <DialogTitle className="font-serif text-3xl font-normal leading-tight">
                Lock in your <span className="italic text-gold">{city}</span>{" "}
                spot.
              </DialogTitle>
              <DialogDescription className="text-[#6B6B6B]">
                Leave your details and we'll hold a Foundation Member seat for
                you the moment Book Away opens in {city}.
              </DialogDescription>
            </DialogHeader>

            <form
              data-testid="waitlist-modal-form"
              onSubmit={onSubmit}
              className="space-y-4"
            >
              <div className="space-y-1.5">
                <Label htmlFor="w_salon" className="text-xs text-[#6B6B6B]">
                  Salon name
                </Label>
                <Input
                  id="w_salon"
                  data-testid="waitlist-modal-salon"
                  value={form.salon_name}
                  onChange={update("salon_name")}
                  placeholder="Maison Noir"
                  required
                  className="border-black/10 bg-white text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="w_owner" className="text-xs text-[#6B6B6B]">
                  Owner name
                </Label>
                <Input
                  id="w_owner"
                  data-testid="waitlist-modal-owner"
                  value={form.owner_name}
                  onChange={update("owner_name")}
                  placeholder="Alex Rivera"
                  required
                  className="border-black/10 bg-white text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="w_email" className="text-xs text-[#6B6B6B]">
                  Email
                </Label>
                <Input
                  id="w_email"
                  type="email"
                  data-testid="waitlist-modal-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@studio.com"
                  required
                  className="border-black/10 bg-white text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="w_cat" className="text-xs text-[#6B6B6B]">
                  Primary category
                </Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
                >
                  <SelectTrigger
                    id="w_cat"
                    data-testid="waitlist-modal-category"
                    aria-required="true"
                    className="border-black/10 bg-white text-[#2A2724] focus:ring-gold data-[placeholder]:text-[#9a9a9a]"
                  >
                    <SelectValue placeholder="What's your primary service?" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="w_phone" className="text-xs text-[#6B6B6B]">
                    Phone <span className="text-[#9a9a9a]">(optional)</span>
                  </Label>
                  <Input
                    id="w_phone"
                    type="tel"
                    data-testid="waitlist-modal-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="0400 000 000"
                    className="border-black/10 bg-white text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="w_ig" className="text-xs text-[#6B6B6B]">
                    Instagram <span className="text-[#9a9a9a]">(optional)</span>
                  </Label>
                  <Input
                    id="w_ig"
                    data-testid="waitlist-modal-instagram"
                    value={form.instagram}
                    onChange={update("instagram")}
                    placeholder="@yoursalon"
                    className="border-black/10 bg-white text-[#2A2724] placeholder:text-[#9a9a9a] focus-visible:ring-gold"
                  />
                </div>
              </div>

              <p className="text-[0.7rem] leading-relaxed text-[#9a9a9a]">
                Leave a phone number if you'd like us to call — otherwise we'll
                stick to email.
              </p>

              {error && (
                <p data-testid="waitlist-modal-error" className="text-sm text-red-600">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                data-testid="waitlist-modal-submit"
                disabled={
                  loading ||
                  !form.salon_name.trim() ||
                  !form.owner_name.trim() ||
                  !form.email.trim() ||
                  !form.category
                }
                className="w-full rounded-full bg-gold py-6 text-sm font-medium text-black hover:bg-[#AA8627] disabled:opacity-60"
              >
                {loading ? "Holding your spot…" : `Join the ${city} waitlist`}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
